// The coverage report (spec/dashboard.md section 9.5): per enabled language the exported strings, the translated and the
// missing ones and the coverage as a bar; a coverage filter; a language's missing keys with the default text and ↗ to the
// drawer; Download missing (CSV) in the shape the import takes back (Key, the default language, the language).
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useContent } from '../../content/ContentContext';
import { languageHeader } from '../../content/localization/languages';
import { coverageOf, downloadText, fullKeyOf, toCsv, useStringRows, valuesOf } from '../../content/localization/strings';
import { EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { BarChart3, CheckCircle2, Download, ExternalLink } from '../../ui/icons';
import { Select } from '../../ui/inputs';
import { useUrlParam } from '../../ui/Modal';
import { useWorkspace } from '../../workspace/react';
import { entityKey } from '../../workspace/Workspace';

export function ReportTab() {
  const catalog = useContent();
  const workspace = useWorkspace();
  const { rows } = useStringRows(catalog);
  const [params, setParams] = useSearchParams();
  const stringParam = useUrlParam('string');
  const live = useMemo(() => rows.filter((r) => workspace.getDraft(entityKey('string', r.id))?.status !== 'deleted'), [rows, workspace]);
  const coverage = useMemo(() => coverageOf(live, catalog.languages, catalog.defaultLanguage), [live, catalog.languages, catalog.defaultLanguage]);
  const filter = params.get('coverage') ?? '';
  const selected = params.get('lang');
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (!v) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const shown = coverage.filter((c) => (filter === 'below100' ? c.percent < 100 : filter === 'below50' ? c.percent < 50 : true));
  const lang = coverage.find((c) => c.code === selected);
  const def = catalog.languages.find((l) => l.code === catalog.defaultLanguage) ?? { code: catalog.defaultLanguage, name: catalog.defaultLanguage };
  const defHeader = languageHeader(def.code, def.name);

  const download = () => {
    if (!lang) return;
    const header = ['Key', defHeader, languageHeader(lang.code, lang.name)];
    const body = lang.missingRows.map((r) => [r.key, valuesOf(r)[def.code] ?? '', '']);
    downloadText(`missing-${lang.code}.csv`, toCsv([header, ...body]));
  };

  const exported = coverage.find((c) => c.isDefault)?.total ?? coverage[0]?.total ?? 0;
  const complete = coverage.filter((c) => c.percent >= 100).length;
  const average = coverage.length > 0 ? Math.round(coverage.reduce((n, c) => n + c.percent, 0) / coverage.length) : 0;

  return (
    <div className="loc-report" data-testid="localization-report">
      <div className="page-toolbar">
        <Select size="s" className="input-inline" aria-label="Coverage" value={filter} onChange={(e) => set({ coverage: e.target.value })}>
          <option value="">All</option>
          <option value="below100">below 100 %</option>
          <option value="below50">below 50 %</option>
        </Select>
        <span className="muted small">Over the exported strings; the default language reads 100 % by definition.</span>
      </div>
      {coverage.length === 0 || live.length === 0 ? (
        <div className="pane">
          <div className="pane-body">
            <EmptyState icon={<BarChart3 />} title="Nothing to report yet" body="The report counts the exported strings per enabled language; this branch has none." />
          </div>
        </div>
      ) : (
        <>
          <div className="stat-strip loc-stats">
            <div className="stat">
              <span className="stat-label">Exported strings</span>
              <span className="stat-value">{exported}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Languages</span>
              <span className="stat-value">{coverage.length}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Complete</span>
              <span className="stat-value">
                {complete} <span className="muted">of {coverage.length}</span>
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Average coverage</span>
              <span className="stat-value">{average} %</span>
            </div>
          </div>
          <div className="pane">
            <div className="pane-head">
              <h2>Coverage by language</h2>
              <span className="muted small">Pick a language to list its missing keys</span>
            </div>
            {shown.length === 0 ? (
              <div className="pane-body">
                <EmptyState icon={<CheckCircle2 />} title="Every language passes this filter" body="No enabled language is below the chosen coverage." />
              </div>
            ) : (
              <table className="grid loc-table">
                <thead>
                  <tr>
                    <th scope="col">Language</th>
                    <th scope="col" className="loc-num">
                      Total
                    </th>
                    <th scope="col" className="loc-num">
                      Translated
                    </th>
                    <th scope="col" className="loc-num">
                      Missing
                    </th>
                    <th scope="col">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.code} className={`grid-row ${c.code === selected ? 'selected loc-selected' : ''}`} data-testid={`coverage-${c.code}`}>
                      <td>
                        <button type="button" className="link-button loc-lang-button" onClick={() => set({ lang: c.code === selected ? null : c.code })} aria-pressed={c.code === selected} aria-label={`Missing keys in ${languageHeader(c.code, c.name)}`}>
                          {languageHeader(c.code, c.name)}
                          {c.isDefault && <span className="chip">default</span>}
                        </button>
                      </td>
                      <td className="loc-num">{c.total}</td>
                      <td className="loc-num">{c.translated}</td>
                      <td className="loc-num">{c.missing}</td>
                      <td>
                        <span className="loc-coverage">
                          <span className="loc-bar" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={c.percent} aria-label={`Coverage of ${c.code}`}>
                            <span className={`loc-bar-fill ${c.percent < 50 ? 'low' : c.percent < 100 ? 'mid' : 'full'}`} style={{ width: `${c.percent}%` }} />
                          </span>
                          <span className="loc-percent">{c.percent} %</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      {lang && (
        <section className="pane loc-missing-pane" data-testid="missing-keys">
          <div className="pane-head">
            <h2>Missing in {languageHeader(lang.code, lang.name)}</h2>
            <span className="chip">{lang.missingRows.length}</span>
            <span className="spacer" />
            <Button size="s" onClick={download} disabled={lang.missingRows.length === 0} icon={<Download aria-hidden="true" />}>
              Download missing (CSV)
            </Button>
          </div>
          {lang.missingRows.length === 0 ? (
            <p className="pane-body muted small">{lang.isDefault ? 'The default language is complete by definition.' : 'Every exported string has a text in this language.'}</p>
          ) : (
            <table className="grid loc-table">
              <thead>
                <tr>
                  <th scope="col">Key</th>
                  <th scope="col">{defHeader}</th>
                  <th scope="col" className="loc-col-actions" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {lang.missingRows.map((r) => (
                  <tr key={r.id} className="grid-row">
                    <td>
                      <code>{fullKeyOf(r)}</code>
                    </td>
                    <td>{valuesOf(r)[def.code] ?? <span className="loc-missing">missing</span>}</td>
                    <td className="loc-num loc-col-actions">
                      <IconButton label={`Open ${fullKeyOf(r)}`} onClick={() => stringParam.open(r.id)}>
                        <ExternalLink aria-hidden="true" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
